<template>
  <v-container v-if="isAdmin">
    <v-btn variant="plain" to="/">
      <v-icon>mdi-arrow-left</v-icon>
      Back
    </v-btn>
    <v-card class="mx-auto mt-4 py-2 px-6">
      <v-card-title class="text-h5">
        Access Requests
      </v-card-title>
      <div>
        <v-checkbox
          v-model="hideSent"
          label="Hide Sent Today"
          class="d-inline-flex"
        ></v-checkbox>
        <v-checkbox
          v-model="allowResend"
          label="Allow Resend"
          class="ml-4 d-inline-flex"
        ></v-checkbox>
        <span class="d-inline-flex">
          <v-select label="Template" :items="emailTemplateNames" v-model="selectedTemplateToSend" density="compact" variant="outlined" hide-details class="ml-4 email-select"></v-select>
        </span>
        <!-- <v-spacer></v-spacer> -->
        <v-btn class="ml-4 mt-n8 d-inline-flex" @click="sendEmailToAll" :disabled="sendingToAll">Send reminder to all</v-btn>
      </div>
      <div v-if="sendingToAll">
        <span>Sending reminder to {{ sendingToAllCount }} people.</span><v-progress-linear v-model="sendingToAllCount" class="ml-4 d-inline-flex" height="10" color="primary" :max="accessRequestsList.length"></v-progress-linear>
      </div>

      <v-data-table
        :headers="requestTableHeaders"
        :items="accessRequestsList"
        :loading="requestsLoading"
      >
        <template v-slot:[`item.timestamp`]="{ item }">
          {{ formatDate(item.timestamp) }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div v-if="item.emailSentAt">
            <v-icon color="green">mdi-check</v-icon>
            {{ formatDate(item.emailSentAt) }}
            <v-btn
              v-if="allowResend"
              color="primary"
              size="small"
              class="mr-2"
              @click="sendEmail(item, selectedTemplateToSend, true)"
            >
              Resend
            </v-btn>
          </div>
          <v-btn
            v-else
            color="primary"
            size="small"
            class="mr-2"
            @click="sendEmail(item, selectedTemplateToSend, true)"

          >
            Send Welcome Email
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
    <v-row class="mt-4">
      <v-col cols="3">
        <v-list>
          <v-list-item><v-btn @click="createNewTemplate">Create New Template</v-btn></v-list-item>
          <v-list-item
            v-for="{ name, template } in emailTemplatesList"
            :key="`${name}-${Date.now()}`"
            :title="name"
            @click="selectedTemplate = template; selectedTemplateName = name"
          ></v-list-item>
        </v-list>
      </v-col>
      <v-col cols="9">
        <v-card class="mx-auto mt-0 py-2 px-6">
          <v-card-title class="text-h5">
            Edit Template
          </v-card-title>
          
          <v-form @submit.prevent="saveTemplate" ref="form" v-if="selectedTemplate != null">
            <v-text-field
              v-model="selectedTemplateName"
              label="Template Name"
              required
              :rules="[v => !!v || 'Name is required']"
            />

            <v-text-field
              v-model="selectedTemplate.from"
              label="From"
              required
              :rules="[v => !!v || 'From address is required']"
            />

            <v-text-field
              v-model="selectedTemplate.subject"
              label="Email Subject"
              required
              :rules="[v => !!v || 'Subject is required']"
            />

            <v-textarea
              v-model="selectedTemplate.html"
              label="HTML Content"
              required
              :rules="[v => !!v || 'HTML is required']"
              rows="10"
            />

            <v-textarea
              v-model="selectedTemplate.text"
              label="Text Content"
              required
              :rules="[v => !!v || 'Text is required']"
              rows="10"
            />

            <v-card-actions>
              <v-spacer />
              <v-btn @click="saveTemplate">Save</v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<script lang="ts">
// import { checkEmail } from '@/utils/general'
import { EmailTemplate } from '@/store'
import { AccessRequest, EmailMessage } from '@/utils/api'
import { sleep } from '@/utils/general'
import { Options, Vue } from 'vue-class-component'
import { mapGetters, mapState } from 'vuex'

export function checkEmail(v: string) {
  return /[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(v)
}

export function checkString(v: string) {
  return /[a-zA-Z0-9]{8}/.test(v)
}

@Options({
  computed: {
    breakpoint(): string {
      return this.$vuetify.display.name
    },
    ...mapState(['username', 'authenticated', 'accessRequests', 'sentAccessRequests', 'isAdmin', 'emailTemplates']),
  },
  watch: {
    breakpoint(value: any) {
      // change layout
    },
    // authenticated(value: boolean) {
    //   if (value) {
        
    //   }
    // }
  }
})
export default class AdminView extends Vue {
  public username!: string
  public isAdmin!: boolean
  public breakpoint!: string
  public authenticated!: boolean
  public email: string = ''
  public referralCode: string = ''
  public hideSent = false
  public allowResend = false

  public accessRequests!: AccessRequest[]
  public sentAccessRequests!: AccessRequest[]
  public requestsLoading = false
  public sendingEmailList = new Map<string, boolean>()
  public emailTemplates!: Record<string, EmailTemplate>
  public selectedTemplate: EmailTemplate | null = null
  public selectedTemplateName: string = ''
  public selectedTemplateToSend: string = ''

  public sendingToAll = false
  public sendingToAllCount = 0
  public sendingToAllTotal = 0

  get accessRequestsList(): AccessRequest[] {
    if (this.hideSent) {
      const today = this.getStartOfTodayInMilliseconds()
      return (this.accessRequests ?? []).filter(r => r.emailSentAt != null && r.emailSentAt < today)
    }
    return this.accessRequests ?? []
  }

  private getStartOfTodayInMilliseconds(): number {
    const now = new Date()
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      0, 0, 0, 0
    )
    
    return startOfToday.getTime()
  }

  get emailTemplatesList(): { name: string, template: EmailTemplate }[] {
    return this.emailTemplates == null ? [] : Array.from(Object.entries(this.emailTemplates)).map(([name, template]) => ({ name, template }))
  }

  get emailTemplateNames(): string[] {
    return this.emailTemplates == null ? [] : Object.keys(this.emailTemplates)
  }

  public getEmailTemplate(name: string): EmailTemplate | undefined {
    if (this.emailTemplates == null) {
      return undefined
    }
    return this.emailTemplates[name]
  }

  public createNewTemplate() {
    this.selectedTemplate = {
      from: '',
      subject: '',
      text: '',
      html: ''
    }
    this.selectedTemplateName = ''
  }

  public saveTemplate() {
    if (this.selectedTemplate == null || this.selectedTemplateName == null) {
      return
    }
    this.saveEmailTempalte(this.selectedTemplateName, this.selectedTemplate)
    // this.selectedTemplate = null
    // this.selectedTemplateName = ''
  }

  public saveEmailTempalte(name: string, template: EmailTemplate) {
    this.$store.dispatch('setEmailTemplate', { name, template })
  }

  get admin() {
    return this.isAdmin ?? false
  }

  public requestTableHeaders = [
    { title: 'Email', key: 'email' },
    { title: 'Request Date', key: 'timestamp' },
    { title: 'Actions', key: 'actions' }
  ]

  public emailRules = [
    (v: string) => !!v || 'Email is required',
    (v: string) => checkEmail(v) || 'Invalid email address.',
  ]

  public referralCodeRules = [
    (v: string) => checkString(v) || 'Invalid referral code.',
  ]

  public async mounted() {
    await this.$store.dispatch('checkAuth')
    await this.$store.dispatch('getNotSignedUp')
  }

  public formatDate(timestamp: number): string {
    return new Date(timestamp).toLocaleString()
  }

  public async sendEmailToAll() {
    console.log('Sending email to all')
    this.sendingToAll = true
    for (const item of this.accessRequestsList) {
      this.sendingToAllCount++
      await this.sendEmail(item, this.selectedTemplateToSend, false)
      await sleep(1000) // Sleep for 1 second to avoid rate limiting
    }
    this.sendingToAll = false
    await this.$store.dispatch('getNotSignedUp')
  }

  public async sendEmail(item: AccessRequest, templateName: string, refresh: boolean = true) {
    console.log(item)
    const template = this.getEmailTemplate(templateName)
    if (!template || item == null || item.email == null) {
      console.error('Error sending email')
      return
    }
    const { from, subject, html, text } = template
    const emailMessage: EmailMessage = {
      // to: 'jayson@missionsquad.ai',
      to: item.email,
      from,
      subject,
      html,
      text
    }
    console.log(emailMessage)
    const response: { success: boolean, result?: any } = await this.$store.dispatch('sendEmail', { emailMessage })
    if (refresh && response.success) {
      // refresh data
      await this.$store.dispatch('getNotSignedUp')
    }
  }

  public sendingEmail(item: AccessRequest): boolean {
    return this.sendingEmailList.get(item.email) || false
  }
}
</script>
<style lang="scss">
.access-text {
  max-width: 400px;
}
.email-select {
  width: 150px;
}
</style>